import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import AppTable from "../../components/ui/AppTable";
import ListToolbar from "../../components/ui/ListToolbar";
import { TypeBadge, ScopeBadge } from "../../components/common/EventDetailModal";
import { formatListDate } from "../../utils/formatters";
import { useUniAdminAuth } from "../../context/UniAdminAuthContext";
import { PERMISSIONS } from "../../utils/permissionKeys";

const UMEventAwaitingList = ({ events, loading, totalElements, onApprove, onReject, onView }) => {
  const { t, i18n } = useTranslation("universityManager");
  const { t: tCommon } = useTranslation("common");
  const { can } = useUniAdminAuth();

  const [rejectModal, setRejectModal] = useState({ open: false, eventId: null, reason: "" });

  const openRejectModal = (eventId) => setRejectModal({ open: true, eventId, reason: "" });
  const closeRejectModal = () => setRejectModal({ open: false, eventId: null, reason: "" });

  const handleRejectConfirm = () => {
    if (!rejectModal.reason.trim()) return;
    onReject(rejectModal.eventId, rejectModal.reason.trim());
    closeRejectModal();
  };

  return (
    <>
      <div className="event-awaiting-list">
        <ListToolbar
          title={t("events.awaitingApproval.list.title")}
          subtitle={t("events.awaitingApproval.list.subtitle", { count: totalElements })}
        />

        <div className="card border-0 shadow-sm">
          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status"><span className="visually-hidden">{tCommon("actions.loading")}</span></div>
                <p className="text-muted mt-2">{t("events.awaitingApproval.list.loading")}</p>
              </div>
            ) : events.length === 0 ? (
              <div className="text-center py-5">
                <i className="fas fa-check-circle text-success mb-3" style={{ fontSize: "3rem" }}></i>
                <h5 className="text-success">{t("events.awaitingApproval.list.allCaughtUp")}</h5>
                <p className="text-muted">{t("events.awaitingApproval.list.noEventsPending")}</p>
              </div>
            ) : (
              <AppTable responsive>
                <thead>
                  <tr>
                    <th className="ps-4">{t("events.awaitingApproval.list.columns.event")}</th>
                    <th>{t("events.awaitingApproval.list.columns.club")}</th>
                    <th>{t("events.awaitingApproval.list.columns.date")}</th>
                    <th className="text-center">{t("events.awaitingApproval.list.columns.typeScope")}</th>
                    <th className="text-center">{t("events.awaitingApproval.list.columns.price")}</th>
                    <th className="text-center pe-4">{t("events.allEvents.list.columns.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => (
                    <tr key={event.id} className="border-bottom">
                      <td className="ps-4">
                        <div className="d-flex align-items-center">
                          <div
                            className="event-icon rounded-circle bg-warning bg-opacity-10 d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                            style={{ width: "45px", height: "45px" }}
                          >
                            <i className="fas fa-calendar-alt text-warning"></i>
                          </div>
                          <div>
                            <div className="fw-bold text-dark">{event.titleLocal || event.titleEn}</div>
                            {event.titleLocal && event.titleEn && <small className="text-muted">{event.titleEn}</small>}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="fw-medium text-dark">{event.clubNameLocal || event.clubNameEn}</div>
                        <small className="text-muted">{event.campusNameLocal || event.campusNameEn}{(event.universityNameLocal || event.universityNameEn) ? `, ${event.universityNameLocal || event.universityNameEn}` : ""}</small>
                      </td>
                      <td>
                        <span className="text-muted">{formatListDate(event.startDate, i18n.language)}</span>
                      </td>
                      <td className="text-center">
                        <div className="d-flex flex-column gap-1 align-items-center">
                          <TypeBadge type={event.participationType} />
                          <ScopeBadge scope={event.participationScope} />
                        </div>
                      </td>
                      <td className="text-center">
                        {event.price === 0 ? (
                          <span className="badge bg-success bg-opacity-10 text-success fw-bold">{t("events.allEvents.list.free")}</span>
                        ) : (
                          <span className="text-dark fw-bold">₺{event.price}</span>
                        )}
                      </td>
                      <td className="text-center pe-4">
                        <div className="d-flex gap-2 justify-content-center align-items-center">
                          {can(PERMISSIONS.event.get) && (
                            <button
                              className="btn btn-sm btn-outline-info"
                              onClick={() => onView && onView(event)}
                              title={tCommon("buttons.viewDetails")}
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                          )}
                          {can(PERMISSIONS.event.approve) && (
                            <button className="btn-modern btn-modern-success" onClick={() => onApprove(event.id)} title={t("events.awaitingApproval.list.approve")}>
                              <i className="fas fa-check"></i>
                              <span className="btn-modern-text">{t("events.awaitingApproval.list.approve")}</span>
                            </button>
                          )}
                          {can(PERMISSIONS.event.reject) && (
                            <button className="btn-modern btn-modern-danger" onClick={() => openRejectModal(event.id)} title={t("events.awaitingApproval.list.reject")}>
                              <i className="fas fa-times"></i>
                              <span className="btn-modern-text">{t("events.awaitingApproval.list.reject")}</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </AppTable>
            )}
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      {rejectModal.open && (
        <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-exclamation-triangle me-2 text-danger"></i>
                  {t("events.awaitingApproval.list.rejectModal.title")}
                </h5>
                <button type="button" className="btn-close" onClick={closeRejectModal}></button>
              </div>
              <div className="modal-body">
                <p className="text-muted mb-3">{t("events.awaitingApproval.list.rejectModal.description")}</p>
                <div className="mb-3">
                  <label className="form-label fw-bold">
                    {t("events.awaitingApproval.list.rejectModal.reasonLabel")} <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className={`form-control ${!rejectModal.reason.trim() ? "is-invalid" : ""}`}
                    rows={4}
                    placeholder={t("events.awaitingApproval.list.rejectModal.reasonPlaceholder")}
                    value={rejectModal.reason}
                    onChange={(e) => setRejectModal((m) => ({ ...m, reason: e.target.value }))}
                    autoFocus
                  />
                  {!rejectModal.reason.trim() && (
                    <div className="invalid-feedback">{t("events.awaitingApproval.list.rejectModal.reasonRequired")}</div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeRejectModal}>
                  {t("events.awaitingApproval.list.rejectModal.cancel")}
                </button>
                <button className="btn btn-danger" onClick={handleRejectConfirm} disabled={!rejectModal.reason.trim()}>
                  <i className="fas fa-times me-2"></i>
                  {t("events.awaitingApproval.list.rejectModal.confirmReject")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UMEventAwaitingList;
